import React, {
    useMemo,
} from 'react'
import moment from 'moment'

import Chip from '@pixel/core/Chip'
import Link from '@pixel/core/Link'
import Typography from '@pixel/core/Typography'

import {Application} from "./useApplicationList";
import { generateViewJobURL } from './PreviewCandidateModal';
import { useMultipleLocalizedText } from '../../../contexts/Localization/localizationHooks';
import LocalizedTypography from "../../LocalizedTypography";
import {LocalizedText} from "../../../contexts/Localization/LocalizationTypes";
import {styled} from "@pixel/core/styles";

export function generateColumnsConfiguration({ activeCandidateApplication, localizations }: { activeCandidateApplication: any, localizations: any }): Array<any> {

    const TalentFitBadge = styled('div')({
        textTransform: 'uppercase',
        backgroundColor: 'white',
        border: '1px solid #197B1E',
        borderRadius: 16,
        fontWeight: 'bold',
        display: 'inline-flex',
        alignItems: 'center',
        padding: '0 7px'
    })

    const columns = [
        { 
            id: 'jobApplied', 
            columnName: localizations.HEADER_JOB, 
            minWidth: 150, 
            renderCell: (node: Application) => (
                <div style={{display: 'flex'}}>
                    <Link
                        href={generateViewJobURL({ jobEId: node.job.EId })}
                        disabled={node.EId === activeCandidateApplication.application.EId}
                        sx={{
                            fontWeight: node.EId === activeCandidateApplication.application.EId ? 800 : undefined,
                        }}
                    >
                        {node.job.title}
                        { node.job.requisitionId && `(${node.job.requisitionId})` }
                    </Link>
                    {window.enableCandidateRanking
                        && window.enableJvTalentFit
                        && activeCandidateApplication?.applicationRankScore?.overallCompatibility
                        && node.EId === activeCandidateApplication.application.EId && (
                        <div style={{ display: 'flex', alignItems: 'center', marginLeft: '10px' }}>
                            <TalentFitBadge>
                                <span className="icon icon-sparklies" style={{ fontSize: '16px' }} />
                                <LocalizedTypography
                                    variant="body1"
                                    text={LocalizedText.TALENT_FIT}
                                    color={'#197B1E'}
                                    fontSize={'12px'}
                                    fontWeight={'bold'}
                                    letterSpacing={'0.08em'}
                                    marginLeft={'5px'}
                                />
                            </TalentFitBadge>
                        </div>
                    )}
                </div>
            ),
        },
        { 
            id: 'status', 
            columnName: localizations.HEADER_STATUS, 
            minWidth: 150, 
            renderCell: (node: Application) => node.applicationStatus, 
        },
        { 
            id: 'lastActivity', 
            columnName: localizations.COMMON_LAST_ACTIVITY, 
            minWidth: 150, 
            renderCell: (node: Application) => moment(node.lastUpdate).format(window._jvselfUser.locale.userDate.shortDate().toUpperCase()), 
        },
    ]

    if (window.properties.isCandidateRankingEnabled  && !(window.enableCandidateRanking && window.enableJvTalentFit)) {
        columns.splice(1, 0, { 
            id: 'match', 
            columnName: localizations.HEADER_JOBMATCH, 
            maxWidth: 120, 
            renderCell: (node: Application) => (
                <>
                    { node.job && node.job.EId ? (
                        node.rankScore != null ? (
                            node.rankScore >= 4 ? (
                                <Chip label={localizations.COMMON_MATCH} sx={{ backgroundColor: '#8237E2', color: 'white' }} />
                            ): (
                                null
                            )
                        ): (
                            <Typography>{localizations.COMMON_PENDING}</Typography>
                        )
                    ): (
                        <Typography>{localizations.COMMON_MATCH_NA}</Typography>
                    ) }
                </>
            ), 
        })
    }

    return columns
}

export function generateSortOrders({ localizations }: { localizations: any }) {
    return [
        { id: 'lastActivity', label: localizations.COMMON_LAST_ACTIVITY, directions: ['asc', 'desc'] },
    ]
}

export function handleSortOrder() {
    const sortOperations = {
        'lastActivity': {
            'asc': (a, b) => a.lastUpdate - b.lastUpdate,
            'desc': (a, b) => b.lastUpdate - a.lastUpdate,
        },
    }

    return function(sortOrder: string, direction: string) {
        return sortOperations[sortOrder][direction]
    }
}

interface PreviewCandidateColumnsProps {
    activeCandidateApplication: any
}
export default function usePreviewCandidateColumns({
    activeCandidateApplication
}: PreviewCandidateColumnsProps) {
    const {
        HEADER_JOB,
        HEADER_STATUS,
        HEADER_JOBMATCH,
        COMMON_LAST_ACTIVITY,
        COMMON_MATCH,
        COMMON_PENDING,
        COMMON_MATCH_NA,
    } = useMultipleLocalizedText()

    const localizations = { 
        HEADER_JOB, 
        HEADER_STATUS, 
        HEADER_JOBMATCH,
        COMMON_LAST_ACTIVITY,
        COMMON_MATCH,
        COMMON_PENDING,
        COMMON_MATCH_NA,
    }

    return useMemo(() => ({
        columns: generateColumnsConfiguration({ activeCandidateApplication, localizations }),
        sortOrders: generateSortOrders({ localizations }),
        sortResults: handleSortOrder(),
    }), [activeCandidateApplication])
}
